import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  approveDeletion,
  approveFamily,
  approveInclusion,
  findOneDeletion,
  findOneFamily,
  findOneInclusion,
  masters,
  openStoredFile,
  uploadToModule,
} from "./api";
import { BOMOF_LABEL, BRAND, DECLARATION_TEXT, DOC_TYPE_LABEL } from "./constants";
import {
  employeeProfile,
  formatAmount,
  fromSapDate,
  fromSapDateTime,
  getOccupationDetailField,
  getSessionUser,
  isSupportedDoc,
  labelOf,
  nonZeroId,
  pick,
  remarkText,
  toRemarkRows,
  unwrapResults,
  yesNo,
  yesNoNa,
  type InclusionKind,
  type Option,
} from "./helpers";
import { ConfirmModal, Field, InfoRow, PageShell, Section, StatusBadge, TextArea } from "./ui";

const NO_DATA = "-";

function OptRow({ label, value }: { label: string; value?: any }) {
  if (value === undefined || value === null || String(value).trim() === "") return null;
  return <InfoRow label={label} value={String(value)} />;
}

function DocumentsBlock({ title, documents }: { title?: string; documents: any[] }) {
  if (!documents.length) return null;
  return (
    <Section title={title || "Uploaded Documents"}>
      {documents.map((doc, idx) => {
        const code = String(doc?.TypeOfDocument ?? "").trim();
        return (
          <button
            key={`${doc?.FileName ?? "doc"}-${idx}`}
            type="button"
            className="w-100 text-start d-flex align-items-center gap-2 py-2 border-0 bg-transparent"
            onClick={() =>
              openStoredFile(doc).catch(() => toast.error("Could not open the document"))
            }
          >
            <i className="ti ti-file-text" style={{ color: BRAND, fontSize: 22 }} />
            <span>
              <span className="d-block fw-semibold">{doc?.FileName || NO_DATA}</span>
              {code && <span className="d-block" style={{ color: "#666", fontSize: 13 }}>{doc?.DocumentDescription || DOC_TYPE_LABEL[code] || code}</span>}
            </span>
            <i className="ti ti-eye ms-auto" style={{ color: BRAND }} />
          </button>
        );
      })}
    </Section>
  );
}

function ParentsDetails({ raw, occupation, siblingOccupation }: { raw: any; occupation: Option[]; siblingOccupation: Option[] }) {
  const bomof = String(raw?.Bomof ?? "");
  const fatherAlive = bomof === "00" || bomof === "11";
  const motherAlive = bomof === "00" || bomof === "12";
  const totalIncome = (fatherAlive ? parseFloat(raw?.FatherMonthlyIncome) || 0 : 0) + (motherAlive ? parseFloat(raw?.MotherMonthlyIncome) || 0 : 0);
  const siblings = unwrapResults(raw?.ToSiblings);
  const spouseOccupation = labelOf(siblingOccupation, raw?.SpouseOccupation);
  const spouseIsDmrc = spouseOccupation.toLowerCase().includes("dmrc");
  const spouseName =
    pick(raw, ["SpouseName"]) ||
    (spouseIsDmrc || String(raw?.IfSpouseSiblingInDMRC ?? "") !== "Y" ? pick(raw, ["NameOfSiblingSpouseInDMRC"]) : "");

  const parent = (who: "Father" | "Mother", alive: boolean) => {
    const lower = who.toLowerCase();
    const occupationLabel = labelOf(occupation, raw?.[`${who}Occupation`]);
    const detail = getOccupationDetailField(occupationLabel === NO_DATA ? "" : occupationLabel, !alive);
    const name = [raw?.[`${who}FirstName`], raw?.[`${who}LastName`]].filter(Boolean).join(" ");
    return (
      <Section title={alive ? `Details of ${lower === "father" ? "Father" : "Mother"}` : `Details of deceased ${lower}`}>
        <InfoRow label="Name" value={name || NO_DATA} />
        <InfoRow label="Date Of Birth" value={fromSapDate(raw?.[`${who}DateOfBirth`]) || NO_DATA} />
        {alive && <InfoRow label="Current Address" value={raw?.[`${who}CurrentAddress`] || NO_DATA} />}
        <InfoRow label={alive ? "Occupation / Source of Income" : "Occupation before death"} value={occupationLabel} />
        {detail.visible && <OptRow label={detail.label} value={raw?.[`${who}DetailsOfEmployer`]} />}
        {alive ? (
          <InfoRow label="Monthly Income" value={formatAmount(raw?.[`${who}MonthlyIncome`])} />
        ) : (
          <InfoRow label="Date of death" value={fromSapDate(raw?.[`${who}DeathDate`]) || NO_DATA} />
        )}
      </Section>
    );
  };

  return (
    <>
      <Section title="Request Information">
        <InfoRow label="Reference No" value={raw?.ReferenceNumber || NO_DATA} />
        <InfoRow label="Employee ID" value={raw?.EmpID || NO_DATA} />
        <InfoRow label="Employee Name" value={pick(raw, ["EmployeeName", "EmpName", "Name"]) || NO_DATA} />
        <InfoRow label="Designation" value={pick(raw, ["Designation"]) || NO_DATA} />
        <InfoRow label="Pay Scale" value={pick(raw, ["PayScale"]) || NO_DATA} />
        <InfoRow label="Contact No." value={raw?.ContactNumber || NO_DATA} />
        <InfoRow label="Basic Pay" value={formatAmount(pick(raw, ["BasicPay", "BasicSalary", "Amount"]))} />
        <InfoRow label="Addition Of" value={BOMOF_LABEL[bomof] ?? (bomof || NO_DATA)} />
        <InfoRow label="Request Creation Date" value={fromSapDate(raw?.RequestCreationDate) || NO_DATA} />
      </Section>
      {parent("Father", fatherAlive)}
      {parent("Mother", motherAlive)}
      {fatherAlive && motherAlive && (
        <Section title="Total Monthly Income of Parents">
          <InfoRow label="Total Monthly Income of Parents" value={totalIncome.toFixed(2)} />
        </Section>
      )}
      <DocumentsBlock documents={unwrapResults(raw?.ToDocument)} />
      {(!!spouseName || spouseOccupation !== NO_DATA) && (
        <Section title="Spouse Details">
          <OptRow label="Name of Spouse" value={spouseName} />
          <InfoRow label="Spouse Occupation" value={spouseOccupation} />
        </Section>
      )}
      <Section title="Whether employee is a single child?">
        <InfoRow label="Whether employee is a single child?" value={yesNo(raw?.IfEmployeeSingleChild)} />
        <InfoRow
          label="Whether any Post Retirement Medical benefit is being availed by parents of employee"
          value={yesNoNa(raw?.IfPostRetirement)}
        />
        {["N", "NO"].includes(String(raw?.IfEmployeeSingleChild ?? "").trim().toUpperCase()) && (
          <InfoRow
            label="Certificate of No claim of LTC/Medical for parents from siblings' employer attached"
            value={yesNoNa(raw?.IfNOCFromSibling)}
          />
        )}
      </Section>
      {toRemarkRows(raw).length === 0 && !!raw?.Remarks && (
        <Section title="Employee Remarks">
          <InfoRow label="Employee Remarks" value={raw.Remarks} />
        </Section>
      )}
      <Section title="Declaration">
        <InfoRow label="Declaration" value={DECLARATION_TEXT} />
      </Section>
      {siblings.length > 0 && (
        <Section title="Details of Siblings">
          {siblings.map((s, idx) => (
            <div key={idx} className={idx ? "mt-3 pt-3" : ""} style={idx ? { borderTop: "1px solid #E2E8F0" } : undefined}>
              <InfoRow label="Sr. No." value={s?.SequenceNumber || String(idx + 1)} />
              <InfoRow label="Name" value={s?.Name || NO_DATA} />
              <InfoRow label="Date Of Birth" value={fromSapDate(s?.DateOfBirth) || NO_DATA} />
              <InfoRow label="Relationship" value={s?.Relationship || NO_DATA} />
              <InfoRow label="Working With" value={labelOf(siblingOccupation, s?.WorkingWith)} />
              <OptRow label="Employee ID" value={nonZeroId(s?.EmpIDOfSibling)} />
              <OptRow label="Details of employer" value={s?.DetailsOfEmployer} />
              <OptRow label="Remarks" value={s?.Remark} />
            </div>
          ))}
        </Section>
      )}
    </>
  );
}

function DeletionDetails({ raw }: { raw: any }) {
  const name = [raw?.RelativeFirstName, raw?.RelativeLastName].filter(Boolean).join(" ") || pick(raw, ["DependentName"]);
  return (
    <>
      <Section title="Request Information">
        <InfoRow label="Reference No" value={raw?.ReferenceNumber || NO_DATA} />
        <InfoRow label="Employee ID" value={raw?.EmpID || NO_DATA} />
        <InfoRow label="Employee Name" value={pick(raw, ["EmployeeName", "EmpName", "Name"]) || NO_DATA} />
        <OptRow label="Pay Scale" value={raw?.PayScale} />
        <OptRow label="Designation" value={raw?.Designation} />
        <OptRow label="Contact No." value={raw?.ContactNumber} />
        <InfoRow label="Action" value="Deletion" />
        <InfoRow label="Request Creation Date" value={fromSapDate(raw?.RequestCreationDate) || NO_DATA} />
        {toRemarkRows(raw).length === 0 && <OptRow label="Remarks" value={pick(raw, ["Reason", "Remarks"])} />}
      </Section>
      <Section title="Dependent Details">
        <InfoRow label="Dependent Name" value={name || NO_DATA} />
        <InfoRow label="Relationship" value={pick(raw, ["Relationship", "RelationshipText", "DependentRelation"]) || NO_DATA} />
        <OptRow label="Date Of Birth" value={fromSapDate(pick(raw, ["DateOfBirth", "DependentDOB"]))} />
      </Section>
      <DocumentsBlock documents={unwrapResults(raw?.ToDocuments)} />
    </>
  );
}

function FamilyDetails({
  raw,
  mastersMap,
}: {
  raw: any;
  mastersMap: Record<string, Option[]>;
}) {
  const isDeletion = String(raw?.FamilyMembersAdditionDeletion ?? "").trim() === "1";
  const relationshipLabel = labelOf(mastersMap.relationship, raw?.Relationship).toLowerCase();
  const isSpouse = relationshipLabel.includes("spouse");
  const isChild = relationshipLabel.includes("child");
  const name = [raw?.RelativeFirstName, raw?.RelativeLastName].filter(Boolean).join(" ");
  const dmrcLabel = isSpouse
    ? "If spouse is working with DMRC, his/her Emp No."
    : isChild
      ? "If child is working with DMRC, his/her Emp No."
      : "If dependent is working with DMRC, his/her Emp No.";
  return (
    <>
      <Section title="Request Information">
        <InfoRow label="Reference No" value={raw?.ReferenceNumber || NO_DATA} />
        <InfoRow label="Employee ID" value={raw?.EmpID || NO_DATA} />
        <InfoRow label="Employee Name" value={pick(raw, ["EmployeeName", "EmpName", "Name"]) || NO_DATA} />
        <InfoRow label="Pay Scale" value={pick(raw, ["PayScale"]) || NO_DATA} />
        <InfoRow label="Designation" value={pick(raw, ["Designation"]) || NO_DATA} />
        <InfoRow label="Contact No." value={raw?.ContactNumber || NO_DATA} />
        {isChild && <InfoRow label="Spouse Name" value={pick(raw, ["SpouseName"]) || NO_DATA} />}
        {!!String(raw?.SpouseDependency ?? "").trim() && (
          <InfoRow label="Spouse dependency status" value={labelOf(mastersMap.dependencyType, raw?.SpouseDependency)} />
        )}
        <InfoRow label="Action" value={isDeletion ? "Deletion" : "Addition"} />
        <InfoRow label="Request Creation Date" value={fromSapDate(raw?.RequestCreationDate) || NO_DATA} />
        {toRemarkRows(raw).length === 0 && <OptRow label="Remarks" value={raw?.Remarks} />}
      </Section>
      <Section title="Dependent Details">
        <InfoRow label="Dependent Name" value={name || NO_DATA} />
        <InfoRow label="Relationship" value={labelOf(mastersMap.relationship, raw?.RelationshipText)} />
        {isDeletion && <InfoRow label="Date Of Birth" value={fromSapDate(raw?.DateOfBirth) || NO_DATA} />}
        {!isDeletion && (
          <>
            <InfoRow label="Gender" value={labelOf(mastersMap.gender, raw?.Gender)} />
            <InfoRow label="Date Of Birth" value={fromSapDate(raw?.DateOfBirth) || NO_DATA} />
            <InfoRow label="Nationality" value={labelOf(mastersMap.nationality, raw?.Nationality)} />
            <InfoRow label="Dependency Type" value={labelOf(mastersMap.dependencyType, raw?.DependencyType)} />
            <InfoRow label="Employment Status of Dependent" value={labelOf(mastersMap.employment, raw?.EmployeeStatus)} />
            <InfoRow label="Staying under same roof" value={labelOf(mastersMap.roof, raw?.StayingRoof)} />
            {!!raw?.ReasonForNotStaying && (
              <InfoRow label="Reason for Not staying under the same roof" value={labelOf(mastersMap.reasonRoof, raw?.ReasonForNotStaying)} />
            )}
            <OptRow label="Marriage Date" value={fromSapDate(raw?.MarriedDate)} />
            {!!raw?.MaritalStatusOfChild && (
              <InfoRow label="Marital Status of dependent" value={labelOf(mastersMap.marital, raw?.MaritalStatusOfChild)} />
            )}
            <OptRow label={dmrcLabel} value={raw?.PersonnelNumber} />
            <OptRow label="Name of the State" value={raw?.NameOfState} />
            <OptRow label="Organization name" value={raw?.OrganizationName} />
            {!!raw?.MonthlyIncome && <InfoRow label="Monthly Income" value={formatAmount(raw?.MonthlyIncome)} />}
          </>
        )}
      </Section>
      <DocumentsBlock title="Photo" documents={unwrapResults(raw?.ToPictures)} />
      <DocumentsBlock documents={unwrapResults(raw?.ToDocuments)} />
    </>
  );
}

const FIND = {
  parents: findOneInclusion,
  delete: findOneDeletion,
  family: findOneFamily,
};
const APPROVE = {
  parents: approveInclusion,
  delete: approveDeletion,
  family: approveFamily,
};
const UPLOAD_MODULE = {
  parents: "inclusion_of_dependents",
  delete: "sap_dependent_deletion",
  family: "sap_inclusion_of_dependents_family",
};

const InclusionApprovalPage = ({ kind, readOnly = false }: { kind: InclusionKind; readOnly?: boolean }) => {
  const { requestID = "" } = useParams();
  const navigate = useNavigate();
  const decodedId = decodeURIComponent(requestID);
  const [raw, setRaw] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [remarks, setRemarks] = useState("");
  const [remarkError, setRemarkError] = useState("");
  const [confirmReject, setConfirmReject] = useState(false);
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState<{ key: string; fileName: string; fileExtension: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [occupation, setOccupation] = useState<Option[]>([]);
  const [siblingOccupation, setSiblingOccupation] = useState<Option[]>([]);
  const [familyMasters, setFamilyMasters] = useState<Record<string, Option[]>>({
    relationship: [],
    gender: [],
    nationality: [],
    dependencyType: [],
    employment: [],
    roof: [],
    reasonRoof: [],
    marital: [],
  });

  const load = useCallback(async () => {
    if (!decodedId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await FIND[kind](decodedId);
      if (res.ok && res.data) setRaw(res.data);
      else {
        setRaw(null);
        toast.error(res.message || "Something went wrong");
      }
    } catch {
      setRaw(null);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [decodedId, kind]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (kind === "parents") {
      masters.occupation().then(setOccupation).catch(() => undefined);
      masters.siblingOccupation().then(setSiblingOccupation).catch(() => undefined);
    }
    if (kind === "family") {
      Promise.all([
        masters.relationship(),
        masters.gender(),
        masters.nationality(),
        masters.dependencyType(),
        masters.employment(),
        masters.roof(),
        masters.reasonRoof(),
        masters.marital(),
      ]).then(([relationship, gender, nationality, dependencyType, employment, roof, reasonRoof, marital]) => {
        setFamilyMasters({ relationship, gender, nationality, dependencyType, employment, roof, reasonRoof, marital });
      });
    }
  }, [kind]);

  const remarkRows = toRemarkRows(raw);

  const submit = async (actionType: "A" | "R") => {
    const user = getSessionUser();
    const payload: Record<string, any> = {
      Action: actionType,
      ReferenceNumber: decodedId,
      EmpID: pick(raw, ["EmpID", "EmpId"]),
      HRemarks: remarks,
      ApproverUserID: String(user?.empId ?? user?.details?.empId ?? employeeProfile(user).employeeNo).padStart(8, "0"),
    };
    if (docs.length > 0) {
      payload.ToDocuments = {
        results: docs.map((d) => ({
          FileName: d.fileName,
          FileExtension: d.fileExtension,
          Content: d.key,
          TypeOfDocument: "D",
        })),
      };
    }
    setBusy(true);
    try {
      const res = await APPROVE[kind](payload);
      if (res?.success && res?.data?.success !== false) {
        toast.success(actionType === "A" ? "Application has been approved" : "Application has been rejected");
        navigate(-1);
      } else toast.error(res?.data?.message || res?.message || "Something went wrong");
    } catch (err: any) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell title="Inclusion and Deletion">
      {loading && <div className="text-center py-5">Loading...</div>}
      {!loading && !raw && <div className="text-center py-5">No details available</div>}
      {raw && (
        <>
          <div className="df-note d-flex align-items-center gap-2">
            <span className="fw-bold">Request Status</span>
            <StatusBadge status={raw?.RequestStatus ?? raw?.Status} />
          </div>
          {kind === "parents" && <ParentsDetails raw={raw} occupation={occupation} siblingOccupation={siblingOccupation} />}
          {kind === "delete" && <DeletionDetails raw={raw} />}
          {kind === "family" && <FamilyDetails raw={raw} mastersMap={familyMasters} />}

          {remarkRows.length > 0 && (
            <Section title="Previous Remarks">
              {remarkRows.map((r, idx) => (
                <div key={idx} className="py-2" style={{ borderBottom: "1px solid #F1F3F7" }}>
                  <div className="fw-semibold">{pick(r, ["ApproverName", "approverName", "Apnam"]) || "-"}</div>
                  <div style={{ color: "#666", fontSize: 13 }}>{pick(r, ["ApproverUserID", "approverUserID", "Apuid"])}</div>
                  <div>{remarkText(r)}</div>
                  <div style={{ color: "#888", fontSize: 12 }}>{fromSapDateTime(pick(r, ["TimeStamp", "timeStamp", "timestamp", "Tstamp"]))}</div>
                </div>
              ))}
            </Section>
          )}

          {!readOnly && (
            <Section title="Upload Documents">
              <label className="btn text-white" style={{ background: BRAND }}>
                {uploading ? "Uploading..." : "Choose File"}
                <input
                  type="file"
                  hidden
                  disabled={uploading}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (!file) return;
                    if (!isSupportedDoc(file)) {
                      toast.error("Only PDF, JPG or PNG files are allowed");
                      return;
                    }
                    setUploading(true);
                    const isPdf = /\.pdf$/i.test(file.name);
                    const mime = isPdf ? "application/pdf" : file.type || "application/octet-stream";
                    const res = await uploadToModule(file, UPLOAD_MODULE[kind], mime);
                    setUploading(false);
                    if (res.ok) {
                      setDocs((prev) => [...prev, { key: res.key, fileName: file.name, fileExtension: mime }]);
                      toast.success("Document uploaded successfully!");
                    } else toast.error(res.message || "Something went wrong");
                  }}
                />
              </label>
              {docs.map((d, idx) => (
                <div key={d.key} className="d-flex justify-content-between align-items-center mt-2">
                  <span>{d.fileName}</span>
                  <button type="button" className="btn btn-link text-danger" onClick={() => setDocs((prev) => prev.filter((_, i) => i !== idx))}>
                    Delete
                  </button>
                </div>
              ))}
            </Section>
          )}

          {!readOnly && (
            <Section title="Your Remarks">
              <Field label="" error={remarkError}>
                <TextArea value={remarks} placeholder="Write Something..." onChange={(e) => setRemarks(e.target.value)} />
              </Field>
            </Section>
          )}

          {!readOnly && (
            <div className="df-actions">
              <button
                type="button"
                className="btn text-white px-5"
                style={{ background: "#16A34A" }}
                disabled={busy}
                onClick={() => {
                  setRemarkError("");
                  submit("A");
                }}
              >
                Approve
              </button>
              <button
                type="button"
                className="btn text-white px-5"
                style={{ background: "#DC2626" }}
                disabled={busy}
                onClick={() => {
                  if (!remarks.trim()) {
                    setRemarkError("Remark is required");
                    return;
                  }
                  setRemarkError("");
                  setConfirmReject(true);
                }}
              >
                Reject
              </button>
            </div>
          )}
        </>
      )}
      <ConfirmModal
        open={confirmReject}
        title="Reject"
        message={"Are you sure?\nYou want to Reject?"}
        onNo={() => setConfirmReject(false)}
        onYes={() => {
          setConfirmReject(false);
          submit("R");
        }}
      />
    </PageShell>
  );
};

export default InclusionApprovalPage;
